/**
 * How a people builds. Eleven peoples, one set of parameters, no bespoke art.
 *
 * ── Why this file exists ────────────────────────────────────────────────────
 *
 * `textures.ts` already knew that architecture was a thing: `ArchSkin` gave each
 * of ten faction *styles* six colours, a crown and a litter type. Two problems
 * with it, and the second is the expensive one.
 *
 * The first is that six colours and a roof cap is a recolour with a hat on. Put
 * a Karrun-Dûr town beside an Ashen one and you got the same buildings twice,
 * once with merlons. The lesson the portrait work paid for — *outline does
 * nearly all the work at small sizes and palette does almost none* — had not
 * reached the buildings.
 *
 * The second is that there were ten styles for eleven races, keyed off
 * `FactionDef.architecture`, so the Silver Reaches and the Verdant Choir were
 * one town, and the Splitmoot and the Cog-Warren were another. A race roster
 * whose whole premise is *two goblin kingdoms must not look alike* cannot be
 * served by a table that cannot tell them apart.
 *
 * ── The structure, and why it is this one ───────────────────────────────────
 *
 * Two axes, and keeping them separate is the whole design. It is the same split
 * `src/ui/racefaces.ts` made for faces and for the same reason:
 *
 *   BuildingShape + motif   what the building *does*. A granary is a fat blind
 *                           drum in every town in the world, because a granary
 *                           is a fat blind drum. Shared by every race, and no
 *                           race may override it
 *   Vernacular              who built it. Material, roof pitch, how far the
 *                           eaves reach, whether the walls lean, whether they
 *                           build up or dig in, what hangs over the door
 *
 * If function leaks into the vernacular — if the dwarves' entry says "granaries
 * are round here" — then the player has to learn eleven vocabularies instead of
 * one, and the first acceptance test (*name the building from its outline*) is
 * lost to buy the second (*tell the two towns apart*). Both are winnable, but
 * only by keeping them on different axes.
 *
 * Eleven races times eighty-eight buildings is nine hundred and sixty-eight
 * drawings, which is not a realistic amount of work and would come out thin if
 * it were attempted. Eleven races times nine parameters is ninety-nine numbers,
 * and every one of them changes all eighty-eight buildings at once.
 *
 * ── What not to put here ────────────────────────────────────────────────────
 *
 * Not a colour, on its own. Every entry below carries six, and they are the
 * least of it; a race distinguished only by its palette is not distinguished.
 * The acceptance counter at the bottom refuses to count colour at all.
 *
 * Not a texture. Coursing, shingles and scales are a handful of marks, never a
 * pattern. A 64px building is four tiles at default zoom and a pattern on it is
 * noise.
 *
 * Not a building. If a people needs its own structure, that is a `BuildingType`
 * with a `race` gate on it, and `races.ts` already has two apiece.
 */

import type { RaceId } from '../data/races.ts';
import type { ArchitectureStyle } from '../data/factions.ts';
import { C } from '../data/palette.ts';

/**
 * How a faction caps its raised mass — the one thing you read of a settlement
 * from across the map, before any wall or roof colour registers.
 */
export type CrownKind =
  | 'battlement'
  | 'gable'
  | 'dome'
  | 'spire'
  | 'cone'
  | 'cap'
  | 'rib'
  | 'lean'
  | 'deck'
  | 'branch'
  | 'flat'
  | 'pagoda';

/** Ground clutter round the footprint. Cheap, and it doubles the read. */
export type LitterKind =
  | 'none'
  | 'stakes'
  | 'roots'
  | 'bones'
  | 'poles'
  | 'spoil'
  | 'caps'
  | 'polyps'
  | 'rubble'
  | 'scrap'
  | 'lamps'
  | 'chains';

/**
 * What the wall is made of, drawn as marks on it rather than as a fill.
 *
 * This is the cheapest per-race read in the file and the only one that survives
 * being half a building wide: four horizontal courses say stone, four verticals
 * say palisade, a scatter of rivets says the Cog-Warren salvaged it.
 */
export type Fabric =
  /** Dressed blocks in regular courses. Straight, level, expensive. */
  | 'ashlar'
  /** Undressed field stone, coursed badly. */
  | 'rubble'
  /** Round logs, laid horizontally, ends showing at the corners. */
  | 'log'
  /** Sawn boards, vertical, with a gap between every third. */
  | 'plank'
  /** Mud over a frame: no coursing at all, and the frame showing through. */
  | 'daub'
  /** Long bones lashed upright. */
  | 'bone'
  /** Stretched skins over a frame, seams stitched. */
  | 'hide'
  /** Shell and coral, grown in whorls rather than laid in courses. */
  | 'shell'
  /** Grown, and still growing — grain that runs the wrong way round openings. */
  | 'bark'
  /** Riveted plate off something that used to be a machine. */
  | 'plate'
  /** Rammed earth, banded where each lift dried. */
  | 'earth';

/** What is fixed over the door, or to the gable end. Always breaks the outline. */
export type Ornament =
  | 'none'
  /** Crossed barge-boards over the ridge. */
  | 'crossed'
  /** A skull and a pair of horns, nailed up. */
  | 'horns'
  /** A hanging lamp on a bracket. */
  | 'lamp'
  /** Ward-marks: three short bars cut into the lintel. */
  | 'wards'
  /** Chains, swagged from the eave corners. */
  | 'chains'
  /** A carved post beside the door, taller than the door. */
  | 'totem'
  /** Whatever was to hand, nailed on at an angle. */
  | 'scrap'
  /** A vent pipe and a guy-wire. */
  | 'pipe'
  /** A rib arch over the lintel. */
  | 'ribs'
  /** Growth: three flowering spurs off the gable. */
  | 'blooms';

/** The shape of every hole cut in every wall. Small, and it reads at any size. */
export type Opening = 'square' | 'arch' | 'round' | 'slit' | 'lancet' | 'ragged';

export interface Vernacular {
  id: string;

  // ── Material ─────────────────────────────────────────────────────────────
  //
  // Six colours, and they are the least of what is here. Everything stays
  // inside the ash-and-bone rule: nothing on a roof may out-value a soldier,
  // because the moment it does the field turns to soup and you cannot find your
  // own men on it.

  wall: number;
  wallLit: number;
  roof: number;
  roofLit: number;
  timber: number;
  /** The one saturated thing a building is allowed: finials, trim, banners. */
  trim: number;
  fabric: Fabric;

  // ── Envelope ─────────────────────────────────────────────────────────────
  //
  // These five are the whole of the silhouette argument. 1 is the Ashen
  // baseline for the two that are ratios; 0 is "not at all" for the three that
  // are amounts. Useful ranges are given per field, and going outside them does
  // not crash, it just stops looking like a building.

  /**
   * Roof pitch, as a multiple of the baseline rise. 0.3 is nearly flat, 1 is a
   * plain gable, 1.9 is a spike you could hurt yourself on.
   *
   * The single most legible number in this file. A skyline of 1.7s and a
   * skyline of 0.4s are two countries at any zoom where a roof is two pixels.
   */
  pitch: number;
  /**
   * How far the eaves reach past the wall, as a fraction of the mass's half
   * width. 0 flush, 0.34 a hat. An overhang throws the wall into shadow and is
   * the difference between "built" and "sheltering".
   */
  eave: number;
  /**
   * Proportion of the raised mass. 0.7 squats and spreads, 1.45 stands up and
   * narrows. Applied to the mass, never to the footprint — the footprint is the
   * building's *rules* and belongs to `BuildingType`.
   */
  stance: number;
  /**
   * How much of the building is in the ground rather than on it. 0 builds up,
   * 0.55 is a doorway in a spoil bank with a chimney behind it.
   *
   * The one parameter that changes the *ground line* as well as the mass, which
   * is why a Karrun-Dûr street reads as a hillside and an Ashen one as a row.
   */
  sunken: number;
  /**
   * Inward lean of the walls per unit of height. 0 plumb, 0.3 a pyramid frustum.
   * Battered walls read as heavy and load-bearing; plumb ones read as framed.
   */
  batter: number;

  // ── Ornament ─────────────────────────────────────────────────────────────

  crown: CrownKind;
  litter: LitterKind;
  ornament: Ornament;
  opening: Opening;
}

/**
 * The Ashen Crown: the baseline every other entry is written as a departure
 * from, in the same way `HUMAN_BASELINE` is for faces.
 *
 * It is deliberately the dullest thing in the file. A tired stone-and-timber
 * town with a shallow roof and square windows is the reference against which
 * "steeper", "lower", "narrower" and "dug in" mean anything at all.
 */
export const ASHEN_BASELINE: Vernacular = {
  id: 'ashen',
  wall: 0x4d4740,
  wallLit: 0x605949,
  roof: 0x39342c,
  roofLit: 0x4a4438,
  timber: 0x4b3d2c,
  trim: 0x6f6558,
  fabric: 'rubble',
  pitch: 1,
  eave: 0.1,
  stance: 1,
  sunken: 0,
  batter: 0.03,
  crown: 'battlement',
  litter: 'rubble',
  ornament: 'crossed',
  opening: 'square',
};

function v(id: string, over: Partial<Vernacular>): Vernacular {
  return { ...ASHEN_BASELINE, ...over, id };
}

/**
 * The fourteen, plus one for the Rotmoot — which is a faction with no race entry
 * and a settlement the player never builds, but which the architecture bank can
 * still be pointed at by a debug console or a battle map.
 *
 * Read down any one column and you should find no two the same. That is not a
 * style note, it is `vernacularsDiffer` below, and there is a test.
 *
 * **The vocabulary is now full and the last three entries had to reuse.** There
 * are twelve crowns and twelve litters and all twelve of each were spoken for
 * before the eastern peoples arrived, so `longnoon`, `undertow` and `thirstless`
 * share a value here and there with an older town. That is what
 * `vernacularsDiffer` is for and why it counts to three rather than demanding a
 * unique column: every one of the three clears four against every other people
 * on the board. A fifteenth race will not have that luxury — it will want new
 * values in `CrownKind` and `LitterKind`, and those are drawing work rather
 * than table work.
 */
export const VERNACULAR: Record<string, Vernacular> = {
  /**
   * Men, tired. Field stone badly coursed, a shallow roof, and merlons on
   * anything important because the last three generations all expected a siege.
   */
  ashen: ASHEN_BASELINE,

  /**
   * A holy order, fallen. Everything reaches: dressed ashlar, a steep roof, a
   * needle over it, lancet windows too narrow to be practical, and a lamp over
   * every door that somebody has to keep filled.
   */
  vigil: v('vigil', {
    wall: 0x615c52,
    wallLit: 0x7a7466,
    roof: 0x453f34,
    roofLit: 0x6b5d3e,
    timber: 0x5a5040,
    trim: C.gold,
    fabric: 'ashlar',
    pitch: 1.62,
    eave: 0.04,
    stance: 1.34,
    batter: 0,
    crown: 'spire',
    litter: 'lamps',
    ornament: 'lamp',
    opening: 'lancet',
  }),

  /**
   * Dwarves. They did not build a town, they cut one: the mass is mostly below
   * the ground line, the walls batter hard, the roof is a slab because a roof is
   * a thing that keeps snow off a chimney and nothing more.
   */
  karrun: v('karrun', {
    wall: 0x4a453c,
    wallLit: 0x5f594a,
    roof: 0x35302a,
    roofLit: 0x453f36,
    timber: 0x4f4132,
    trim: 0xa8703a,
    fabric: 'ashlar',
    pitch: 0.34,
    eave: 0.02,
    stance: 0.72,
    sunken: 0.34,
    batter: 0.26,
    crown: 'flat',
    litter: 'spoil',
    ornament: 'wards',
    opening: 'square',
  }),

  /**
   * Wood elves. Nothing here was cut to a line: bark-grained walls, an
   * overhanging canopy rather than a roof, spurs going up out of the mass, and
   * openings that the grain runs round instead of through.
   */
  verdant: v('verdant', {
    wall: 0x3a4232,
    wallLit: 0x4a543d,
    roof: 0x2b3325,
    roofLit: 0x3d4931,
    timber: 0x494a2f,
    trim: 0x6a7845,
    fabric: 'bark',
    pitch: 1.28,
    eave: 0.3,
    stance: 1.12,
    batter: 0,
    crown: 'branch',
    litter: 'roots',
    ornament: 'blooms',
    opening: 'round',
  }),

  /**
   * The Silver Reaches. The same kin as the Choir and not remotely the same
   * people: pale sawn board on a frame, very tall, very thin, eaves out to
   * nothing, and a tiered cap because they build in stages and never in a lump.
   */
  silver: v('silver', {
    wall: 0x6c6a63,
    wallLit: 0x86847a,
    roof: 0x4a4d50,
    roofLit: 0x63666a,
    timber: 0x7d7a6c,
    trim: 0xb9c6cc,
    fabric: 'plank',
    pitch: 1.05,
    eave: 0.33,
    stance: 1.45,
    batter: 0,
    crown: 'pagoda',
    litter: 'none',
    ornament: 'lamp',
    opening: 'arch',
  }),

  /**
   * The Drowned Court. Not built and not grown — accreted. Whorled shell,
   * round mouths for doors, a lumpy mass with growth going up out of it, and
   * the ground round it wet enough to have things living in it.
   */
  drowned: v('drowned', {
    wall: 0x3e4750,
    wallLit: 0x4f5a63,
    roof: 0x2f3841,
    roofLit: 0x445059,
    timber: 0x475059,
    trim: 0x6a8490,
    fabric: 'shell',
    pitch: 0.86,
    eave: 0.22,
    stance: 1.06,
    sunken: 0.14,
    batter: 0.1,
    crown: 'dome',
    litter: 'polyps',
    ornament: 'none',
    opening: 'round',
  }),

  /**
   * The Crimson Assize. Dark render over stone, roofs like knives, everything
   * arched and everything chained — the chains are the read, and they are on the
   * building because of what is kept inside it.
   */
  crimson: v('crimson', {
    wall: 0x3b3238,
    wallLit: 0x4c414a,
    roof: 0x241d22,
    roofLit: 0x382c33,
    timber: 0x463a3c,
    trim: 0x8e3a3a,
    fabric: 'ashlar',
    pitch: 1.86,
    eave: 0.06,
    stance: 1.28,
    batter: 0.02,
    crown: 'gable',
    litter: 'chains',
    ornament: 'chains',
    opening: 'arch',
  }),

  /**
   * The Splitmoot. Nobody has ever finished a roof. One slope, too steep, held
   * down with whatever was heavy, and a ring of stakes round it because the
   * thing most likely to knock it over is the neighbours.
   */
  splitmoot: v('splitmoot', {
    wall: 0x3f352b,
    wallLit: 0x4f4233,
    roof: 0x2c241c,
    roofLit: 0x3d3126,
    timber: 0x4f4030,
    trim: 0x6a4a34,
    fabric: 'daub',
    pitch: 1.44,
    eave: 0.26,
    stance: 0.82,
    batter: 0,
    crown: 'lean',
    litter: 'stakes',
    ornament: 'scrap',
    opening: 'ragged',
  }),

  /**
   * The Cog-Warren. The other goblins, and the point of having two: riveted
   * plate off something that used to work, flat decks instead of roofs, a vent
   * pipe on everything, and the whole warren half sunk into its own diggings.
   */
  cogwarren: v('cogwarren', {
    wall: 0x453d33,
    wallLit: 0x5a4f40,
    roof: 0x37312a,
    roofLit: 0x4a4137,
    timber: 0x5c4a34,
    trim: 0x8a7a3c,
    fabric: 'plate',
    pitch: 0.42,
    eave: 0.16,
    stance: 0.94,
    sunken: 0.26,
    batter: 0.06,
    crown: 'deck',
    litter: 'scrap',
    ornament: 'pipe',
    opening: 'square',
  }),

  /**
   * The Pale Litany. Bone lashed upright, a ribcage over the roof, arches
   * everywhere because an arch is a thing that stands without anybody holding
   * it up. Nothing here has been repaired in a very long time.
   */
  litany: v('litany', {
    wall: 0x635b4d,
    wallLit: 0x7a7160,
    roof: 0x4a4438,
    roofLit: 0x5f5747,
    timber: 0x565042,
    trim: 0x9a917c,
    fabric: 'bone',
    pitch: 1.16,
    eave: 0.12,
    stance: 1.2,
    batter: 0,
    crown: 'rib',
    litter: 'bones',
    ornament: 'ribs',
    opening: 'arch',
  }),

  /**
   * The Moonbound. They keep no walls, and a wall is a thing you die behind.
   * Hide over poles, a cone that goes up to a smoke hole, a carved post by the
   * door taller than the door, and everything moveable.
   */
  moonbound: v('moonbound', {
    wall: 0x4a3b30,
    wallLit: 0x5c4a3a,
    roof: 0x3a2e25,
    roofLit: 0x4e3f2f,
    timber: 0x6a5a45,
    trim: 0x796950,
    fabric: 'hide',
    pitch: 1.7,
    eave: 0.0,
    stance: 0.9,
    batter: -0.18,
    crown: 'cone',
    litter: 'poles',
    ornament: 'horns',
    opening: 'ragged',
  }),

  /**
   * Saurians. They did not build up, they laid *out*: broad stone risers stepping
   * back from the water, roofed only where a roof is unavoidable, and battered
   * hard because the mass is doing the work rather than the frame. A carved post
   * stands beside every door and is taller than the door. Slit openings, because
   * the point of a wall here is shade.
   */
  longnoon: v('longnoon', {
    wall: 0x6f6752,
    wallLit: 0x8a8064,
    roof: 0x4e4636,
    roofLit: 0x6a5f45,
    timber: 0x5c4c30,
    trim: 0xd0a838,
    fabric: 'ashlar',
    pitch: 0.28,
    eave: 0.2,
    stance: 0.78,
    sunken: 0,
    batter: 0.2,
    crown: 'deck',
    litter: 'roots',
    ornament: 'totem',
    opening: 'slit',
  }),

  /**
   * Ratkin, over water. Round logs driven into mud and then built on twice more,
   * every eave dripping into the next roof down, chains swagged between the
   * gables because that is how you get from one to another when the tide is in.
   * The only town in the bank that is *partly* below its own ground line without
   * having been dug.
   */
  undertow: v('undertow', {
    wall: 0x4f4636,
    wallLit: 0x675b45,
    roof: 0x3a3428,
    roofLit: 0x554b38,
    timber: 0x6a5a3c,
    trim: 0x9c8f3a,
    fabric: 'log',
    pitch: 0.9,
    eave: 0.34,
    stance: 0.68,
    sunken: 0.2,
    batter: 0,
    crown: 'gable',
    litter: 'stakes',
    ornament: 'chains',
    opening: 'square',
  }),

  /**
   * Giants. Rammed sandstone at a scale nobody else has a use for, walls leaning
   * in hard enough to read as a hillside, and most of the mass under the sand
   * because most of it has been there long enough for the sand to arrive. The
   * openings are arches because an arch is what holds up that much of anything.
   */
  thirstless: v('thirstless', {
    wall: 0x746850,
    wallLit: 0x8e8062,
    roof: 0x5a5040,
    roofLit: 0x6f6350,
    timber: 0x655a44,
    trim: 0xc9b48a,
    fabric: 'earth',
    pitch: 0.5,
    eave: 0.06,
    stance: 1,
    sunken: 0.42,
    batter: 0.32,
    crown: 'dome',
    litter: 'spoil',
    ornament: 'crossed',
    opening: 'arch',
  }),

  /**
   * The Rotmoot, which has no race entry and never will — nobody plays a
   * parliament of sores. Kept because the bank can be pointed at any faction.
   */
  rotmoot: v('rotmoot', {
    wall: 0x453f34,
    wallLit: 0x565044,
    roof: 0x3c3c28,
    roofLit: 0x504f34,
    timber: 0x4a4132,
    trim: 0x6b7639,
    fabric: 'earth',
    pitch: 0.66,
    eave: 0.34,
    stance: 0.86,
    sunken: 0.12,
    batter: 0.16,
    crown: 'cap',
    litter: 'caps',
    ornament: 'blooms',
    opening: 'ragged',
  }),
};

/**
 * The faction fallback.
 *
 * `FactionDef.architecture` is the older half of this idea and is still what a
 * battle map, a debug spawn and any enemy settlement have to hand. Every style
 * maps onto the vernacular of whichever people actually builds that way, so
 * nothing regressed when the key changed from ten styles to eleven peoples —
 * the only thing that *changed* is that the two goblin races and the two elven
 * pairs stopped sharing an entry, which was the point.
 */
const STYLE_TO_VERNACULAR: Record<ArchitectureStyle, string> = {
  stone: 'ashen',
  timber: 'ashen',
  grown: 'verdant',
  bone: 'litany',
  hide: 'moonbound',
  gilded: 'vigil',
  crude: 'splitmoot',
  burrow: 'cogwarren',
  fungal: 'rotmoot',
  coral: 'drowned',
};

/** The vernacular of a people, by `RaceId`. Falls back to the baseline. */
export function vernacularOf(raceId: RaceId | string): Vernacular {
  return VERNACULAR[raceId] ?? ASHEN_BASELINE;
}

/** The vernacular a faction's architecture style stands in for. */
export function vernacularForStyle(style: ArchitectureStyle): Vernacular {
  return VERNACULAR[STYLE_TO_VERNACULAR[style] ?? 'ashen'] ?? ASHEN_BASELINE;
}

/**
 * How many ways two peoples build differently. The acceptance rule, counted
 * rather than remembered.
 *
 * Five categorical axes and five numeric ones. A numeric axis counts only when
 * the gap is large enough to see at the size a building is actually drawn —
 * a roof one twentieth steeper is not a different country.
 *
 * **Colour is deliberately not counted.** Two entries differing only in their
 * six colours score zero here, which is the correct answer and the one the
 * portrait pass had to learn the expensive way.
 *
 * Three is the bar. One is a recolour, two is a variant, three is a people.
 */
export function vernacularsDiffer(a: Vernacular, b: Vernacular): number {
  let n = 0;
  if (a.fabric !== b.fabric) n++;
  if (a.crown !== b.crown) n++;
  if (a.litter !== b.litter) n++;
  if (a.ornament !== b.ornament) n++;
  if (a.opening !== b.opening) n++;
  if (Math.abs(a.pitch - b.pitch) >= 0.3) n++;
  if (Math.abs(a.stance - b.stance) >= 0.18) n++;
  if (Math.abs(a.sunken - b.sunken) >= 0.15) n++;
  if (Math.abs(a.batter - b.batter) >= 0.1) n++;
  if (Math.abs(a.eave - b.eave) >= 0.12) n++;
  return n;
}
